import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/**
 * Composant SEOHead pour gérer les meta tags dynamiques par page
 *
 * @param {string} title - Titre de la page
 * @param {string} description - Description de la page
 * @param {string} keywords - Mots-clés SEO (optionnel)
 * @param {string} canonical - URL canonique, chemin relatif ou URL absolue (optionnel)
 * @param {{ fr?: string, en?: string }} alternateUrls - URLs hreflang par langue (optionnel)
 * @param {string} ogImage - Image Open Graph (optionnel)
 * @param {string} ogType - Type Open Graph (optionnel, défaut: website)
 * @param {string} twitterCard - Type de Twitter Card (optionnel, défaut: summary_large_image)
 * @param {boolean} noindex - Exclure la page de l'indexation (défaut: false)
 */
const SEOHead = ({
  title = 'Assistance Leagora - Dépannage client à distance',
  description = 'Dépannez vos clients à distance par vidéo en un clic. Aucune application à télécharger. QR code, géolocalisation GPS, qualité HD. Essai gratuit sans engagement.',
  keywords = 'assistance vidéo, dépannage à distance, support technique vidéo, QR code assistance, télé-assistance, SAV vidéo, support client vidéo',
  canonical = '',
  alternateUrls = null,
  ogImage = '/og-image.jpg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noindex = false
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'fr';

  const baseUrl = 'https://assistance.leagora.io';

  const normalizeToFullUrl = (value) => {
    if (!value) {
      return `${baseUrl}/`;
    }

    if (value.startsWith('http')) {
      return value;
    }

    return `${baseUrl}${value.startsWith('/') ? value : `/${value}`}`;
  };

  const fullCanonical = normalizeToFullUrl(canonical);
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  const hreflangFr = normalizeToFullUrl(alternateUrls?.fr ?? canonical);
  const hreflangEn = normalizeToFullUrl(alternateUrls?.en ?? canonical);
  const hreflangDefault = normalizeToFullUrl(alternateUrls?.fr ?? canonical);

  const localeMap = {
    fr: 'fr_FR',
    en: 'en_US'
  };

  const ogLocale = localeMap[currentLang] || 'fr_FR';
  const languageName = currentLang === 'fr' ? 'French' : 'English';

  return (
    <Helmet>
      <html lang={currentLang} />

      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={fullCanonical} />

      <link rel="alternate" hreflang="fr" href={hreflangFr} />
      <link rel="alternate" hreflang="en" href={hreflangEn} />
      <link rel="alternate" hreflang="x-default" href={hreflangDefault} />

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Assistance Leagora" />
      <meta property="og:locale" content={ogLocale} />
      {currentLang === 'fr' && <meta property="og:locale:alternate" content="en_US" />}
      {currentLang === 'en' && <meta property="og:locale:alternate" content="fr_FR" />}

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />

      <meta name="author" content="Leagora" />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="language" content={languageName} />
    </Helmet>
  );
};

export default SEOHead;
